import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiHelper } from '../../../../core/services/apiHelper';
import { AdminAccess } from '../../../shared/accessAdmin';
import { UserDAO } from '../../../../core/services/dao/userDAO';
import { VesselDAO } from '../../../../core/services/dao/vesselDAO';
import { CaptainDAO } from '../../../../core/services/dao/captainDAO';
import { ListGridComponent } from '../../../shared/listGrid/listGrid.component';
import { WaterBodyDAO } from '../../../../core/services/dao/waterBodyDAO';
import { BoatOwnerDAO } from '../../../../core/services/dao/boatOwnerDAO';
import { BoatOwner } from 'anchor-shared/lib/model/entity/boatOwner';
import { ApproveButtonComponent } from '../../../shared/listGrid/approveButton/approveButton.component';
import { RejectButtonComponent } from '../../../shared/listGrid/rejectButton/rejectButton.component';
import { sortCaseInsensitive } from '../../../../core/pure-functions/grids/grid-sorting-helper';
import { Observable, Subscription } from 'rxjs';
import { ContainsSubscriptions } from '../../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import { UserSearchResult } from 'anchor-shared/lib/model/interface/userSearchResult';
import { UserSearchDialogService } from '../../../shared/dialogs/user-search-dialog/user-search-dialog-service';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { ConstantMan } from '../../../../core/services/constantMan';

@Component({
  selector: 'app-vessels',
  templateUrl: './vessels.component.html',
  styleUrls: ['./vessels.component.scss'],
})
export class VesselsComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  subs: Subscription[] = [];
  public columnDefs;
  public rowData;
  public editPath;
  public boatOwners: BoatOwner[];

  @ViewChild(ListGridComponent)
  grid: ListGridComponent;

  constructor(
    private router: Router,
    private vesselDAO: VesselDAO,
    private userDAO: UserDAO,
    private apiHelper: ApiHelper,
    private checkAdmin: AdminAccess,
    private captainDao: CaptainDAO,
    private route: ActivatedRoute,
    private waterBodyDAO: WaterBodyDAO,
    private boatOwnerDAO: BoatOwnerDAO,
    private userSearchDialogService: UserSearchDialogService,
  ) {
    const boatOwnersSub = this.boatOwnerDAO
      .loadApprovedOwners()
      .subscribe((boatOwners) => {
        this.boatOwners = boatOwners;
      });
    this.subs.push(boatOwnersSub);
    this.checkAdmin.checkAdminAccess(this.userDAO, this.apiHelper, this.router);
    this.searchFxn = this.searchFxn.bind(this);
  }

  ngOnInit() {
    const los = this.route.snapshot.queryParams.los;
    const status = this.route.snapshot.queryParams.status;
    this.vesselDAO.loadForAdminGrid(los ? los : status).subscribe((data) => {
      this.columnDefs = this.createColumnDefs();
      this.rowData = data;
      this.editPath = 'manage/admin/vessel';
    });
  }

  searchFxn(searchText): Observable<UserSearchResult[]> {
    return this.boatOwnerDAO.adminBoatOwnerSearch(searchText);
  }

  addVessel(): void {
    const sub = this.userSearchDialogService
      .open({ searchFxn: this.searchFxn, userType: 'Boat Owners' })
      .subscribe((userSelection) => {
        if (userSelection) {
          const data = {
            ownerId: userSelection.ownerId,
            status: ConstantMan.VESSEL_STATUSES.PENDING,
            anchorManaged: false,
          };
          this.vesselDAO.createVesselAdmin(data).subscribe((vessel: Vessel) => {
            this.router.navigate(['manage', 'admin', 'vessel', vessel.id]);
          });
        }
      });
    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  private createColumnDefs() {
    const los = this.route.snapshot.queryParams.los;
    const status = this.route.snapshot.queryParams.status;
    if (los) {
      if (los === 'charter') {
        return [
          {
            headerName: 'Name',
            field: 'name',
            filter: 'text',
            minWidth: 50,
            comparator: sortCaseInsensitive,
          },
          {
            headerName: 'Owner',
            field: 'ownerName',
            filter: 'text',
            comparator: sortCaseInsensitive,
          },
          {
            headerName: 'Make',
            field: 'make',
            filter: 'text',
            comparator: sortCaseInsensitive,
          },
          {
            headerName: 'Model',
            field: 'model',
            filter: 'text',
            comparator: sortCaseInsensitive,
          },
          {
            headerName: 'location',
            field: 'locations',
          },
          {
            headerName: 'Active',
            field: 'status',
            filter: 'text',
            suppressSizeToFit: true,
          },
        ];
      }
    } else if (status) {
      return [
        {
          headerName: 'Name',
          field: 'name',
          filter: 'text',
          minWidth: 50,
          comparator: sortCaseInsensitive,
        },
        {
          headerName: 'Owner',
          field: 'ownerName',
          filter: 'text',
          comparator: sortCaseInsensitive,
        },
        {
          headerName: 'Make',
          field: 'make',
          filter: 'text',
          comparator: sortCaseInsensitive,
        },
        {
          headerName: 'Model',
          field: 'model',
          filter: 'text',
          comparator: sortCaseInsensitive,
        },
        {
          headerName: 'Status',
          field: 'status',
          filter: 'text',
        },
        {
          headerName: 'location',
          field: 'locations',
          filter: 'text',
        },
        {
          headerName: 'Active',
          field: 'status',
          filter: 'text',
          suppressSizeToFit: true,
        },
      ];
    } else {
      return [
        {
          headerName: 'Name',
          field: 'name',
          filter: 'text',
          minWidth: 50,
          comparator: sortCaseInsensitive,
        },
        {
          headerName: 'Owner',
          field: 'ownerName',
          filter: 'text',
          comparator: sortCaseInsensitive,
        },
        {
          headerName: 'Make',
          field: 'make',
          filter: 'text',
          comparator: sortCaseInsensitive,
        },
        {
          headerName: 'Model',
          field: 'model',
          filter: 'text',
          comparator: sortCaseInsensitive,
        },
        {
          headerName: 'location',
          field: 'locations',
          filter: 'text',
        },
      ];
    }
  }
}
